import React from 'react';
import Icon from 'components/AppIcon';
import { useNavigate } from 'react-router-dom';

const PLANS = [
  {
    id: 'free',
    name: 'Free',
    price: '$0',
    period: 'forever',
    color: '#6B7280',
    icon: 'User',
    features: [
      '120 video seconds / month',
      'Max 30 seconds per video',
      'Watermarked export',
      'Basic templates',
      'Community access',
    ],
    cta: 'Current Plan',
    current: true,
  },
  {
    id: 'pro',
    name: 'Pro',
    price: '$12',
    period: '/month',
    color: '#6366F1',
    icon: 'Zap',
    popular: true,
    features: [
      '1,800 video seconds / month',
      'Max 60 seconds per video',
      'HD export — no watermarks',
      'All creation modes',
      'Video editor access',
      'Priority generation',
    ],
    cta: 'Upgrade to Pro',
  },
  {
    id: 'studio',
    name: 'Studio',
    price: '$29',
    period: '/month',
    color: '#F59E0B',
    icon: 'Star',
    features: [
      '7,200 video seconds / month',
      'Max 3 minutes per video',
      'Priority rendering',
      'Everything in Pro',
      'AI Music Generation',
      'Marketplace selling',
      'Creator monetization',
    ],
    cta: 'Upgrade to Studio',
  },
  {
    id: 'enterprise',
    name: 'Enterprise',
    price: '$99',
    period: '/month',
    color: '#10B981',
    icon: 'Building2',
    features: [
      '20,000 video seconds / month',
      'Max 5 minutes per video',
      'Priority rendering + faster GPU',
      'Everything in Studio',
      'Dedicated support',
      'API access',
      'White-label options',
    ],
    cta: 'Upgrade to Enterprise',
  },
];

export default function UpgradeModal({ onClose, currentTier = 'free', blockedReason = null, requestedSeconds = null }) {
  const navigate = useNavigate();
  const handleUpgrade = () => {
    onClose?.();
    navigate('/pricing');
  };

  // Determine which plan to highlight based on blocked reason
  const suggestedPlanId = (() => {
    if (!blockedReason) return null;
    if (blockedReason === 'duration_exceeds_plan') {
      if (requestedSeconds <= 60) return 'pro';
      if (requestedSeconds <= 180) return 'studio';
      return 'enterprise';
    }
    if (blockedReason === 'quota_exceeded') {
      if (currentTier === 'free') return 'pro';
      if (currentTier === 'pro') return 'studio';
      return 'enterprise';
    }
    return null;
  })();

  return (
    <div
      className="fixed inset-0 z-[200] flex items-center justify-center p-4"
      style={{ background: 'rgba(0,0,0,0.7)', backdropFilter: 'blur(8px)' }}
      onClick={(e) => e?.target === e?.currentTarget && onClose?.()}
    >
      <div
        className="w-full max-w-4xl max-h-[90vh] overflow-y-auto rounded-2xl"
        style={{ background: 'var(--color-card)', border: '1px solid var(--color-border)', boxShadow: 'var(--shadow-xl)' }}
      >
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b" style={{ borderColor: 'var(--color-border)' }}>
          <div>
            <h2 className="text-xl font-bold" style={{ color: 'var(--color-foreground)', fontFamily: 'var(--font-heading)' }}>
              Upgrade Your Plan
            </h2>
            <p className="text-sm mt-1" style={{ color: 'var(--color-muted-foreground)', fontFamily: 'var(--font-caption)' }}>
              {blockedReason === 'duration_exceeds_plan'
                ? `Your current plan doesn't support ${requestedSeconds}s videos. Upgrade to unlock longer generation.`
                : blockedReason === 'quota_exceeded' ? "You've used all your video seconds for this period. Upgrade for more quota."
                : 'Unlock longer videos and more video seconds per month.'}
            </p>
          </div>
          <button
            onClick={onClose}
            className="w-9 h-9 rounded-xl flex items-center justify-center hover:bg-[var(--color-muted)] transition-all"
          >
            <Icon name="X" size={18} color="var(--color-muted-foreground)" />
          </button>
        </div>

        {/* Quota info banner */}
        <div className="mx-6 mt-4 p-3 rounded-xl flex items-center gap-3" style={{ background: 'rgba(99,102,241,0.08)', border: '1px solid rgba(99,102,241,0.2)' }}>
          <Icon name="Clock" size={16} color="#6366F1" />
          <p className="text-xs" style={{ color: 'var(--color-foreground)', fontFamily: 'var(--font-caption)' }}>
            <strong>Video Seconds Billing:</strong> 1 generated second = 1 credit. A 30s video costs 30 credits, a 60s video costs 60 credits.
          </p>
        </div>

        {/* Plans grid */}
        <div className="p-6 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
          {PLANS?.map((plan) => {
            const isCurrent = plan?.id === currentTier;
            const isSuggested = plan?.id === suggestedPlanId;
            return (
              <div
                key={plan?.id}
                className="relative flex flex-col rounded-2xl p-4 transition-all"
                style={{
                  background: isSuggested ? `${plan?.color}12` : plan?.popular ? `${plan?.color}08` : 'var(--color-muted)',
                  border: `2px solid ${isCurrent ? plan?.color : isSuggested ? plan?.color : plan?.popular ? `${plan?.color}60` : 'var(--color-border)'}`,
                  boxShadow: isSuggested ? `0 0 0 2px ${plan?.color}40` : plan?.popular ? `0 0 0 1px ${plan?.color}30` : 'none',
                }}
              >
                {isSuggested && !isCurrent && (
                  <div
                    className="absolute -top-3 left-1/2 -translate-x-1/2 px-3 py-0.5 rounded-full text-xs font-bold"
                    style={{ background: plan?.color, color: 'white', fontFamily: 'var(--font-caption)' }}
                  >
                    Recommended
                  </div>
                )}
                {plan?.popular && !isSuggested && (
                  <div
                    className="absolute -top-3 left-1/2 -translate-x-1/2 px-3 py-0.5 rounded-full text-xs font-bold"
                    style={{ background: plan?.color, color: 'white', fontFamily: 'var(--font-caption)' }}
                  >
                    Most Popular
                  </div>
                )}
                {isCurrent && (
                  <div
                    className="absolute -top-3 left-1/2 -translate-x-1/2 px-3 py-0.5 rounded-full text-xs font-bold"
                    style={{ background: plan?.color, color: 'white', fontFamily: 'var(--font-caption)' }}
                  >
                    Current
                  </div>
                )}
                {/* Plan header */}
                <div className="flex items-center gap-2 mb-3">
                  <div className="w-8 h-8 rounded-lg flex items-center justify-center" style={{ background: `${plan?.color}20` }}>
                    <Icon name={plan?.icon} size={16} color={plan?.color} />
                  </div>
                  <span className="font-bold text-sm" style={{ color: 'var(--color-foreground)', fontFamily: 'var(--font-heading)' }}>
                    {plan?.name}
                  </span>
                </div>
                {/* Price */}
                <div className="mb-4">
                  <span className="text-2xl font-bold" style={{ color: plan?.color, fontFamily: 'var(--font-heading)' }}>
                    {plan?.price}
                  </span>
                  <span className="text-xs" style={{ color: 'var(--color-muted-foreground)', fontFamily: 'var(--font-caption)' }}>
                    {plan?.period}
                  </span>
                </div>
                {/* Features */}
                <ul className="flex flex-col gap-1.5 mb-4 flex-1">
                  {plan?.features?.map((f) => (
                    <li key={f} className="flex items-start gap-1.5 text-xs" style={{ color: 'var(--color-foreground)', fontFamily: 'var(--font-caption)' }}>
                      <Icon name="Check" size={11} color={plan?.color} className="mt-0.5 flex-shrink-0" />
                      {f}
                    </li>
                  ))}
                </ul>
                {/* CTA */}
                <button
                  onClick={handleUpgrade}
                  className="w-full py-2 rounded-xl text-xs font-bold transition-all hover:opacity-90 disabled:opacity-60 disabled:cursor-default"
                  style={{
                    background: isCurrent ? 'var(--color-muted)' : plan?.color,
                    color: isCurrent ? 'var(--color-muted-foreground)' : 'white',
                    fontFamily: 'var(--font-heading)',
                  }}
                >
                  {plan?.cta}
                </button>
              </div>
            );
          })}
        </div>

        {/* Legal note */}
        <div className="px-6 pb-6">
          <p className="text-xs text-center" style={{ color: 'var(--color-muted-foreground)', fontFamily: 'var(--font-caption)' }}>
            All plans include content safety filtering and legal compliance. Cancel anytime.
          </p>
        </div>
      </div>
    </div>
  );
}
